import { isNotNullish } from '@adornis/assert/isNullish.js';
import type { Maybe } from '@adornis/base/utilTypes.js';
import type { NarrowedEntity } from '@adornis/baseql/utils/queryGeneration.js';
import { confirmDialog, guardDialog } from '@adornis/dialog/dialog-functions.js';
import { DateInput } from '@adornis/formfield/fields/date-input.js';
import { TextInput } from '@adornis/formfield/fields/text-input.js';
import { stateful, useObservable, useState } from '@adornis/functional-lit/stateful.js';
import { hoverTooltip } from '@adornis/popover/hover-tooltip.js';
import { currentUser } from '@adornis/users/client/currentUser.js';
import { formatCalendarDay } from '@adornis/datetime/calendarDay.js';
import { CardHeader } from '@adornis/components/client/CardHeader.js';
import { FormGridEntry } from '@martinus/styles/client/FormGridEntry.js';
import { CardOptionsMenu } from '@martinus/styles/client/menus/CardOptionsMenu.js';
import { StatusBadge } from '@martinus/styles/client/StatusBadge.js';
import { html, nothing } from 'lit';
import { SgSnackbar } from '../../../client/components/ui/sg-snackbar.js';
import { useMartinusABAC } from '../../../permissions/client/useMartinusAbac.js';
import { Role } from '../../../permissions/roles.js';
import type { Person } from '../../../users/entities/person.js';
import type { UserOnUserPayload } from '../../../users/UserPolicy.js';
import { getMinAndMaxDatesOfSupplement } from '../../api/getMinAndMaxDatesOfSupplement.js';
import { ContractsActions } from '../../ContractsPolicy.js';
import type { ContractSupplement, ContractSupplementChange } from '../../entities/ContractSupplement.js';
import { ChangeList } from './changes/ChangeEntry.js';

export type SupplementEditSupplement = NarrowedEntity<
  ContractSupplement,
  {
    _id: 1;
    nachtragsNummer: 1;
    subNachtragsnummer: 1;
    changes: { _id: 1; path: 1; value: 1; type: 1 };
    startDateAG1: 1;
    endDateAG1: 1;
    startDateGeneral: 1;
    endDateGeneral: 1;
    startDateAG2: 1;
    endDateAG2: 1;
    signedAt: 1;
    paragraph: 1;
    automaticallyAssignedEndDateOfContract: 1;
  }
>;

interface SupplementEditProps {
  supplement: Maybe<SupplementEditSupplement>;
  additionalChanges?: Maybe<Array<NarrowedEntity<ContractSupplementChange, { _id: 1; path: 1; value: 1; type: 1 }>>>;
  // only needed for button migration to end the current supplement with the contract enddate
  possibleContractEndDate?: Maybe<Date>;
  onEditToggled?: (isEditing: boolean) => void;
  onDelete?: () => void;
  /** the outer view holds the save button and reads the dirty state at render time, so every edit here has to reach it */
  onUpdateOuter?: () => void;
}

type SupplementDateKey =
  'startDateGeneral' | 'endDateGeneral' | 'startDateAG1' | 'endDateAG1' | 'startDateAG2' | 'endDateAG2';

const DateIntervall = (props: {
  supplement: SupplementEditSupplement;
  start: SupplementDateKey;
  end: SupplementDateKey;
  category: string;
  disabled: boolean;
  onChanged: () => void;
}) => {
  const startValue = props.supplement[props.start];
  const endValue = props.supplement[props.end];

  const timeframeValid = props.supplement.changes?.find(v => v.path.includes(props.category))
    ? (startValue && !endValue) || (startValue && endValue && startValue < endValue)
    : true;

  return html`
    <div class="flex flex-col gap-1">
      <div
        class="flex flex-row items-center gap-1 rounded transition-colors ${
          timeframeValid ? '' : 'border border-error p-1'
        }"
      >
        <div class="min-w-0 flex-1">
          ${DateInput({
            disabled: props.disabled,
            clearable: true,
            placeholder: 'Start',
            placeholderMode: 'hide',
            value: startValue ?? undefined,
            onPicked: (date: Date | null) => {
              props.supplement[props.start] = date;
              props.onChanged();
            },
          })}
        </div>
        <span class="font-semibold">-</span>
        <div class="min-w-0 flex-1">
          ${DateInput({
            disabled: props.disabled,
            clearable: true,
            placeholder: 'Offenes Ende',
            placeholderMode: 'hide',
            value: endValue ?? undefined,
            onPicked: (date: Date | null) => {
              props.supplement[props.end] = date;
              props.onChanged();
            },
          })}
        </div>
      </div>
      ${
        timeframeValid
          ? nothing
          : html`<span class="text-sm text-error">
              Dieser Bereich wird geändert, daher wird hier ein Startdatum benötigt.
            </span>`
      }
    </div>
  `;
};

/**
 * Shows a single supplement with its timeframes, signature state and the changes it makes.
 * Editing is only possible for users with the AG_SEKTION_W permission (or admins).
 */
export const SupplementEdit = stateful((props: SupplementEditProps) => {
  const user = useObservable(() => currentUser<Person>(), []);
  const { can: canEditSupplement } = useMartinusABAC<UserOnUserPayload>(
    { action: ContractsActions.EDIT_SUPPLEMENT, viewedUser: null },
    [],
  );
  const { can: canSignSupplement } = useMartinusABAC<UserOnUserPayload>(
    { action: ContractsActions.SIGN_SUPPLEMENT, viewedUser: null },
    [],
  );

  // a not yet signed supplement starts in editing mode. Reset when another supplement is selected.
  const [isEditing, setIsEditing] = useState(!props.supplement?.signedAt, [props.supplement?._id]);
  const [rerenderCount, setRerenderCount] = useState(0);
  // the values of the supplement itself (dates, signature, paragraph) are dirty state the outer view cannot see:
  // it evaluates `isDirty()` while rendering, so without notifying it the save button would never appear for them
  const rerender = () => {
    setRerenderCount(rerenderCount + 1);
    props.onUpdateOuter?.();
  };

  if (!props.supplement) return html`<div class="skeleton h-24 w-full"></div>`;
  const supplement = props.supplement;

  const lowestStartDate = getMinAndMaxDatesOfSupplement(supplement).start;

  const fieldsDisabled = !isEditing && !!supplement._id;

  const canEdit =
    (supplement._id && canEditSupplement && !supplement.automaticallyAssignedEndDateOfContract) ||
    (supplement._id && user?.roles.find(r => r.name === Role.ADMIN));

  // supplements created before the automatic termination existed have to be ended by hand
  const canEndSupplementManually =
    !supplement.automaticallyAssignedEndDateOfContract &&
    !!props.possibleContractEndDate &&
    !!lowestStartDate &&
    lowestStartDate < props.possibleContractEndDate;

  return html`
    <div class="card card-body">
      ${CardHeader({
        title: html`
          ${!supplement._id ? 'Potenzieller neuer' : ''} Nachtrag
          ${supplement.nachtragsNummer}${supplement.subNachtragsnummer ? `.${supplement.subNachtragsnummer}` : ''}
        `,
        actionsDivider: true,
        actions: html`
          ${
            supplement.automaticallyAssignedEndDateOfContract
              ? StatusBadge({
                  tone: 'error',
                  label: 'Durch Kündigung beendet',
                  iconClass: 'icon-[material-symbols--block]',
                })
              : nothing
          }
          ${
            canEdit
              ? html`
                  <button
                    class="btn btn-sm transition-colors ${isEditing ? 'btn-success' : 'btn-ghost'}"
                    aria-label="Nachtrag bearbeiten"
                    @click=${() => {
                      setIsEditing(!isEditing);
                      props.onEditToggled?.(!isEditing);
                    }}
                  >
                    <span class="icon-[material-symbols--edit-outline] text-lg"></span>
                    ${isEditing ? 'Bearbeitung beenden' : 'Bearbeiten'}
                  </button>

                  ${CardOptionsMenu({
                    ariaLabel: 'Weitere Aktionen für den Nachtrag',
                    items: [
                      {
                        label: 'Nachtrag beenden',
                        iconifyClass: 'icon-[material-symbols--bolt]',
                        intent: 'danger',
                        disabled: !canEndSupplementManually,
                        onClick: async () => {
                          if (!props.possibleContractEndDate) return;
                          if (
                            !(await confirmDialog(
                              'Der Vertrag ist zum ' +
                                formatCalendarDay(props.possibleContractEndDate, 'dd.MM.yyyy') +
                                ' beendet, aber der Nachtrag hat dieses Datum nicht gesetzt, da er älter ist als das Feature. Wollen Sie den Nachtrag manuell beenden?',
                            ))
                          ) {
                            return;
                          }

                          supplement.automaticallyAssignedEndDateOfContract = props.possibleContractEndDate;

                          await guardDialog(() => (supplement as ContractSupplement).save(), {
                            success: () => void SgSnackbar.show('Nachtrag beendet'),
                          });
                        },
                      },
                      {
                        label: 'Nachtrag löschen',
                        iconifyClass: 'icon-[material-symbols--delete-outline]',
                        intent: 'danger',
                        onClick: () => props.onDelete?.(),
                      },
                    ],
                  })}
                `
              : nothing
          }
        `,
      })}

      <!-- one column: the panel is too narrow for a label/field split, so labels sit above their fields -->
      <div class="grid grid-cols-1 gap-1">
        ${FormGridEntry({
          label: 'Paragraph',
          render: TextInput({
            disabled: fieldsDisabled,
            clearable: true,
            placeholder: '§',
            placeholderMode: 'prefix',
            value: supplement.paragraph ?? undefined,
            onPicked: (value: string | null) => {
              supplement.paragraph = value;
              rerender();
            },
          }),
        })}
        ${FormGridEntry({
          label: 'Aktiv Allgemeine Infos',
          render: DateIntervall({
            supplement,
            start: 'startDateGeneral',
            end: 'endDateGeneral',
            category: 'agInfos',
            disabled: fieldsDisabled,
            onChanged: rerender,
          }),
        })}
        ${FormGridEntry({
          label: 'Aktiv Eingruppierung 1',
          render: DateIntervall({
            supplement,
            start: 'startDateAG1',
            end: 'endDateAG1',
            category: 'agEingruppierung1',
            disabled: fieldsDisabled,
            onChanged: rerender,
          }),
        })}
        ${FormGridEntry({
          label: 'Aktiv Eingruppierung 2',
          render: DateIntervall({
            supplement,
            start: 'startDateAG2',
            end: 'endDateAG2',
            category: 'agEingruppierung2',
            disabled: fieldsDisabled,
            onChanged: rerender,
          }),
        })}
      </div>

      <!-- sections inside the Nachtrag card sit one step below its CardHeader title -->
      <div class="flex flex-wrap items-center justify-between gap-2 border-b border-base-300 pb-1">
        <h4 class="font-semibold">Unterschrift</h4>
        ${
          supplement.signedAt
            ? StatusBadge({
                tone: 'success',
                label: `Unterzeichnet am ${formatCalendarDay(supplement.signedAt, 'dd.MM.yyyy')}`,
                iconClass: 'icon-[material-symbols--check-circle-outline]',
              })
            : StatusBadge({
                tone: 'warning',
                label: 'Nicht unterzeichnet',
                iconClass: 'icon-[material-symbols--draft-outline]',
              })
        }
      </div>
      ${DateInput({
        disabled: !isEditing || !canSignSupplement,
        clearable: true,
        placeholder: 'Unterschrieben am',
        placeholderMode: 'prefix',
        value: supplement.signedAt ?? undefined,
        onPicked: (date: Date | null) => {
          supplement.signedAt = date;
          rerender();
        },
      })}
      ${
        supplement._id && props.additionalChanges?.length
          ? html`
              <div class="flex min-h-0 flex-col gap-2">
                <div class="flex items-center justify-between gap-2 border-b border-base-300 pb-1">
                  <h4 class="font-semibold">Änderungen auf dem Nachtrag</h4>
                  <div class="tooltip" ${hoverTooltip()}>
                    <div class="tooltip-content max-w-[600px] text-left">
                      Hier werden Änderungen angezeigt, welche den Nachtrag selbst ändern würden. Für diese Art
                      Änderungen wird ein neuer Nachtrag mit einer fortlaufenden Subnachtrags-Nummer erstellt. Der
                      Subnachtrag bekommt nur die in dieser Sektion angezeigten Änderungen und wird die initial die
                      gleiche Laufzeit wie der Hauptnachtrag besitzen.
                    </div>
                    <span class="icon-[material-symbols--info-outline] text-xl text-base-content/70"></span>
                  </div>
                </div>
                <div class="max-h-[30vh] overflow-y-auto">
                  ${ChangeList({ changes: props.additionalChanges, dense: true })}
                </div>
              </div>
            `
          : nothing
      }

      <div class="flex min-h-0 flex-col gap-2">
        <h4 class="border-b border-base-300 pb-1 font-semibold">Änderungen auf dem Vertrag</h4>
        <div class="max-h-[45vh] overflow-y-auto">
          ${ChangeList({
            changes: supplement.changes?.filter(c => c.value !== 'null'),
            dense: true,
            deletable: isEditing && !!supplement._id,
            emptyState: html`<p class="text-sm text-base-content/70">Noch keine Änderungen erfasst.</p>`,
            onDelete: change => {
              const indexOfChange = supplement.changes?.findIndex(c => c._id === change._id);
              if (isNotNullish(indexOfChange) && indexOfChange >= 0) {
                supplement.changes?.splice(indexOfChange, 1);
                rerender();
              }
            },
          })}
        </div>
      </div>
    </div>
  `;
});
